import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './pages/Login';
import TeacherDashboard from './pages/TeacherDashboard';
import AdminDashboard from './pages/AdminDashboard';
import Home from './pages/Home';
import EngineerDashboard from './components/EngineerDashboard';

const ProtectedRoute = ({ allowedRoles }) => {
  const { user, token, isLoading } = useAuth();

  if (isLoading) return <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading...</div>;

  if (!token) return <Navigate to="/login" />;

  // Role-Based Access Control
  if (allowedRoles && user && !allowedRoles.includes(user.role)) {
    if (user.role === 'admin' || user.role === 'sub_admin') return <Navigate to="/admin/dashboard" replace />;
    if (user.role === 'faculty') return <Navigate to="/dashboard" replace />;
    if (user.role === 'hardware_engineer') return <Navigate to="/engineer/dashboard" replace />;
  }

  return <Outlet />;
};

const RedirectIfAuthenticated = ({ children }) => {
  const { user, token, isLoading } = useAuth();

  if (isLoading) return <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Checking Session...</div>;

  if (token && user) {
    if (user.role === 'admin' || user.role === 'sub_admin') return <Navigate to="/admin/dashboard" />;
    if (user.role === 'hardware_engineer') return <Navigate to="/engineer/dashboard" />;
    return <Navigate to="/dashboard" />;
  }
  return children;
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <Routes>
          {/* Public Home Page - Redirect to Dashboard if Logged In */}
          <Route path="/" element={
            <RedirectIfAuthenticated>
              <Home />
            </RedirectIfAuthenticated>
          } />

          {/* Faculty Login */}
          <Route path="/login" element={
            <RedirectIfAuthenticated>
              <Login title="Faculty Login" subtitle="Secure access for teachers and center staff." type="faculty" />
            </RedirectIfAuthenticated>
          } />

          {/* Admin Login */}
          <Route path="/admin" element={
            <RedirectIfAuthenticated>
              <Login title="Admin Portal" subtitle="Authorized administrator access only." type="admin" />
            </RedirectIfAuthenticated>
          } />

          {/* Engineer Login */}
          <Route path="/engineer" element={
            <RedirectIfAuthenticated>
              <Login title="Hardware Engineer Login" subtitle="Secure access for authorized maintenance staff." type="admin" />
            </RedirectIfAuthenticated>
          } />

          {/* Protected Faculty Routes */}
          <Route element={<ProtectedRoute allowedRoles={['faculty', 'teacher', 'lf', 'cc']} />}>
            <Route path="/dashboard" element={<TeacherDashboard />} />
          </Route>

          {/* Protected Admin Routes — sub_admin also gets access */}
          <Route element={<ProtectedRoute allowedRoles={['admin', 'sub_admin']} />}>
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
          </Route>

          {/* Protected Engineer Routes */}
          <Route element={<ProtectedRoute allowedRoles={['hardware_engineer']} />}>
            <Route path="/engineer/dashboard" element={<EngineerDashboard />} />
          </Route>
        </Routes>
      </AuthProvider>
    </Router>
  );
}

export default App;
